import React from 'react';
import useGlobalStoreContext from '../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import { SessionContext } from '../SessionContextProvider/useSessionContext';
import EndSessionDialogWrapper from './Components/EndSessionDialogWrapper';
import SessionComplianceContextProvider from './Contexts/SessionComplianceContextProvider';
import useSessionComplianceContext from './Contexts/SessionComplianceContextProvider/useSessionComplianceContext';
import SessionStoreContextProvider from './Contexts/SessionStoreContextProvider/SessionStoreContextProvider';
import useSessionStoreContext from './Contexts/SessionStoreContextProvider/useSessionStoreContext';
import AudioWebsocketApiConnectionContextProvider from './Contexts/WebsocketConnectionProviders/AudioWebsocketApiConnectionContext/AudioWebsocketApiConnectionContextProvider';
import WebsocketApiConnectionContextProvider from './Contexts/WebsocketConnectionProviders/WebsocketApiConnectionContext/WebsocketApiConnectionContextProvider';
import useAudioWebsocketConnectionLiveliness from './Hooks/useAudioWebsocketConnectionLiveliness';
import useComplianceNotifications from './Hooks/useComplianceNotifications';
import useComplianceRejection from './Hooks/useComplianceRejection';
import useComplianceReminder from './Hooks/useComplianceReminder/useComplianceReminder';
import useDetectionNotifications from './Hooks/useDetectionNotifications';
import useHandleAppReloadMessage from './Hooks/useHandleAppReloadMessage';
import HandleAudioCaptureProvider from './Hooks/HandleAudioCaptureProvider';
import useHandleContextUpdate from './Hooks/useHandleContextUpdate';
import useHandleDetection from './Hooks/useHandleDetection';
import useHandleExternalCallEvents from './Hooks/useHandleExternalCallEvents';
import useHandleNoVadDetected from './Hooks/useHandleNoVadDetected';
import useHandleSessionActivityTrigger from './Hooks/useHandleSessionActivityTrigger';
import useHandleSessionChange from './Hooks/useHandleSessionChange';
import useHandleSessionTermination from './Hooks/useHandleSessionTermination';
import useHandleSessionTimeout from './Hooks/useHandleSessionTimeout';
import useHandleTopicMention from './Hooks/useHandleTopicMention';
import useSendAlternativeTranscript from './Hooks/useSendAlternativeTranscript/useSendAlternativeTranscript';
import useSessionInfo from './Hooks/useSessionInfo';
import useDetectionNavigation from './Hooks/useSessionNavigation';
import useStartSession from './Hooks/useStartSession';
import useStopSession from './Hooks/useStopSession';
import useUpdateDetectionHistory from './Hooks/useUpdateDetectionHistory';
import useForwardSessionState from './Hooks/useForwardSessionState';

function Inner({ children }: { children: React.ReactNode }) {
  const session = useSessionInfo();
  const {
    sessionLoading: [sessionLoading], terminating: [terminating], sessionId: [sessionId], detectionHistory: [detectionHistory], focusMode: [focusMode, setFocusMode],
  } = useSessionStoreContext();
  const stopSession = useStopSession();
  const { projectId: [projectId] } = useGlobalStoreContext();
  const startSession = useStartSession();
  const {
    latestDetection: [latestDetection],
    context: [context, setContext],
  } = useSessionStoreContext();
  const compliance = useSessionComplianceContext();

  const value = React.useMemo(() => ({
    sessionId,
    session,
    sessionLoading: sessionLoading || !!terminating,
    startSession() {
      if (projectId) {
        startSession({ projectId });
      } else {
        console.error('Tried to start a session but projectId is not defined');
      }
    },
    stopSession() { stopSession('ManualSessionTermination'); },
    context,
    setContext,
    compliance,
    latestDetection,
    detectionHistory,
    focusMode,
    setFocusMode() { setFocusMode((prev) => !prev); },
  }), [sessionId, session, startSession, stopSession, context, setContext, compliance, latestDetection, focusMode, setFocusMode,
    detectionHistory, sessionLoading, terminating,
  ]);

  useHandleSessionActivityTrigger();
  useHandleNoVadDetected();
  useHandleSessionTimeout();
  useHandleSessionTermination();
  useHandleExternalCallEvents();
  useHandleSessionChange();
  useHandleAppReloadMessage();
  useDetectionNavigation();
  useHandleDetection();
  useHandleContextUpdate();
  useSendAlternativeTranscript();
  useHandleTopicMention();
  useComplianceRejection();
  useComplianceReminder();
  useComplianceNotifications();
  useDetectionNotifications();
  useUpdateDetectionHistory();
  useAudioWebsocketConnectionLiveliness();
  useForwardSessionState();

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>

  );
}

/**
 * Explanation regarding the structure:
 * - The SessionStore is responsible for storing the data of the session. It has no logic and is only used for storing data.
 * - Underneath are "Complex context providers". They are context providers for which some information must be stored but cannot be split between the data and the logic.
 * - At the bottom are the "Context Updaters". They are responsible for the underlying logic of the context.
 */
export default function NewSessionContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <SessionStoreContextProvider>
      <WebsocketApiConnectionContextProvider>
        <AudioWebsocketApiConnectionContextProvider>
          <SessionComplianceContextProvider>
            <HandleAudioCaptureProvider>
              <Inner>
                <EndSessionDialogWrapper>
                  {children}
                </EndSessionDialogWrapper>
              </Inner>
            </HandleAudioCaptureProvider>
          </SessionComplianceContextProvider>
        </AudioWebsocketApiConnectionContextProvider>
      </WebsocketApiConnectionContextProvider>
    </SessionStoreContextProvider>
  );
}
